import { type HTMLAttributes, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "senf-shared/globalState/GlobalState";
import { generateLink, getPhaseOfDateRange, openLink } from "senf-shared/index";
import type { Project } from "senf-shared/types";
import { cn } from "../../../../util/classNameConstructor";
import Button from "../../../core/button/Button";
import Dropdown from "../../../core/dropdown/Dropdown";
import type { IconName } from "../../../core/icon/names";
import Toast from "../../../core/toast/Toast";
import { useSocialLinks } from "../../../organisms/socialmediaShare/SocialmediaShare";
import type { Page } from "../survey.config";

type OutroButtonProps = {
  type?: "addPost";
  ideaMapTitle?: string;
  label: string;
  url?: string;
  onClick?: () => void;
  trailingIcon?: IconName;
};

type UseOutroButtonsParams = {
  mode: "edit" | "display";
  project: Project;
  currentPage?: Page;
};

function useOutroButtons({
  mode,
  project,
  currentPage,
}: UseOutroButtonsParams) {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;
  const outroButtons = useMemo(() => {
    if (currentPage?.type !== "outro") return [];

    const buttons: OutroButtonProps[] = [];

    // Info page button
    if (
      project?.infoPages?.length > 0 &&
      currentPage?.settings?.buttons?.infoPage !== false
    ) {
      buttons.push({
        label: t("util:toThe", { val: t("global:infoPage") }),
        url: generateLink("mainApp", `projects/${project.projectId}/info`),
      });
    }

    // Idea map buttons
    for (const ideaMap of project?.ideaMaps || []) {
      const phase = getPhaseOfDateRange(ideaMap.startDate, ideaMap.endDate);
      const isActive =
        mode === "display"
          ? phase === "while"
          : phase === "while" || phase === "before";

      if (
        ideaMap.settings?.publicAccess === false ||
        !isActive ||
        currentPage?.settings?.buttons?.dialogMap === false
      ) {
        continue;
      }

      buttons.push({
        type: "addPost",
        ideaMapTitle: translation?.[ideaMap.title ?? ""] || ideaMap.title || "",
        label: t("survey:successPage.addPost"),
        url: generateLink(
          "mainApp",
          `projects/${project.projectId}/ideaMaps/${ideaMap.ideaMapId}/addIdea`,
        ),
      });
    }

    return buttons;
  }, [mode, project, currentPage, translation, t]);

  return outroButtons;
}
interface SocialShareProps extends HTMLAttributes<HTMLDivElement> {
  mode: "edit" | "display";
  previewLink: string;
  setShowPreviewWarning?: React.Dispatch<React.SetStateAction<boolean>>;
}
const SocialShare = ({
  className,
  mode,
  previewLink,
  setShowPreviewWarning,
}: SocialShareProps) => {
  const { socialLinks } = useSocialLinks();
  const handleClick = (onClick: () => void) => {
    if (mode === "edit") {
      setShowPreviewWarning?.(true);
    } else if (onClick) {
      onClick();
    }
  };
  return (
    <div className={cn("flex gap-2", className)}>
      {socialLinks({
        path: previewLink,
        trackLocation: "shareSurvey",
        filterOut: ["qr-code"] as const,
      }).map(({ leadingIcon, onClick, id }) => (
        <Button
          variant="outline"
          size="xl"
          leadingIcon={leadingIcon}
          onClick={() => handleClick(onClick)}
          key={`social-${id}`}
        />
      ))}
    </div>
  );
};

const OutroButton = ({
  label,
  mode,
  url,
  onClick,
  trailingIcon,
  setShowPreviewWarning,
}: {
  label: string;
  mode?: "edit" | "display";
  url?: string;
  onClick?: () => void;
  trailingIcon?: IconName;
  setShowPreviewWarning?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [translation] = useGlobalState().translationsState;
  const handleClick = () => {
    if (mode === "edit") {
      setShowPreviewWarning?.(true);
    } else if (onClick) {
      onClick();
    } else if (url) {
      openLink(url);
    }
  };
  return (
    <Button
      size="xl"
      text={translation?.[label] || label}
      onClick={handleClick}
      trailingIcon={trailingIcon || "arrowhead-right-md"}
      classNames={{ wrapper: "shrink-0" }}
    />
  );
};
export const Outro = ({
  page,
  pageAlignment,
  className,
  project,
  mode,
  surveyId,
}: {
  page: Page<"outro">;
  pageAlignment: "left" | "center" | "right";
  className: string;
  project: Project;
  mode: "edit" | "display";
  surveyId: string;
}) => {
  const [translation] = useGlobalState().translationsState;
  const { t } = useTranslation();
  const [showPreviewWarning, setShowPreviewWarning] = useState(false);
  useEffect(() => {
    if (showPreviewWarning) setTimeout(() => setShowPreviewWarning(false));
  }, [showPreviewWarning]);

  const buttons = useOutroButtons({ mode, project, currentPage: page });

  const { addPostButtons, otherButtons } = buttons.reduce(
    (acc, button) => {
      if (button.type === "addPost") {
        acc.addPostButtons.push(button);
      } else {
        acc.otherButtons.push(button);
      }
      return acc;
    },
    {
      addPostButtons: [],
      otherButtons: [],
    } as Record<string, OutroButtonProps[]>,
  );

  return (
    <>
      <Toast id={`preview-mode-warning-${page.uid}`} show={showPreviewWarning}>
        <div className="flex flex-col">
          <strong>{t("global:previewModeWarning.title")}</strong>
          {t("global:previewModeWarning.description")}
        </div>
      </Toast>
      {page.settings.buttons.socialMedia && (
        <div className="flex flex-col gap-3">
          <span
            className={cn(
              "body-md-bold",
              pageAlignment === "center" ? "text-center" : "",
            )}
          >
            {t("survey:shareOnSocialMedia")}:
          </span>
          <SocialShare
            className={className}
            mode={mode}
            setShowPreviewWarning={setShowPreviewWarning}
            previewLink={generateLink(
              "mainApp",
              `projects/${project?.projectId}/survey/${surveyId}`,
            )}
          />
        </div>
      )}
      <div className={cn("mt-2 flex flex-col gap-2", className)}>
        {otherButtons.map((props) => (
          <OutroButton
            {...props}
            key={props.label}
            mode={mode}
            setShowPreviewWarning={setShowPreviewWarning}
          />
        ))}

        {addPostButtons.length > 1 ? (
          <Dropdown
            variant="basic"
            size="xl"
            trailingIcon="arrowhead-right-md"
            text={
              translation?.[addPostButtons[0]?.label] ||
              addPostButtons[0]?.label
            }
            items={addPostButtons.map((item) => {
              return {
                text: item?.ideaMapTitle,
                onClick: () =>
                  mode === "edit"
                    ? setShowPreviewWarning(true)
                    : item?.onClick?.(),
              };
            })}
          />
        ) : (
          addPostButtons.map((props) => (
            <OutroButton
              {...props}
              key={props.label}
              mode={mode}
              setShowPreviewWarning={setShowPreviewWarning}
            />
          ))
        )}

        {page?.settings?.buttons?.custom?.map((props) => (
          <OutroButton
            {...props}
            key={props.label}
            mode={mode}
            setShowPreviewWarning={setShowPreviewWarning}
          />
        ))}
      </div>
    </>
  );
};
